/**
 * SKY SCENE — composes the cartoon sky (flat bands, sparkle stars, the inked sun or moon,
 * puffy clouds, the banded sea and its glare) for an hour of the sky clock.
 *
 * Two clocks drive it:
 * - the in-game hour: colours, the orbit, cloud drift and star drift (decor time-lapses with
 *   the hour and holds still when the hour does);
 * - the pause-aware wobble clock (`opts.wobbleSec`, seconds): the squash and stretch of the
 *   sun and moon, the ray spin, cloud lobe morphing, star twinkle and the sea's lapping. It
 *   runs at a steady pace whatever the hour does, stops on pause, and is held constant when
 *   the sky is static so snapshots stay valid.
 *
 * Sandboxes and the Canvas 2D fallback (jsdom / no-WebGL) draw through this module. Live play
 * uses the Pixi sky layer, fed the same layouts by render/sky/shader-uniforms.js.
 */

import { drawSky, skyColorAt } from "./sky/sky-gradient.js";
import { drawSea, drawSeaGlare } from "./sky/sea.js";
import { drawStars, starFieldAlpha } from "./sky/stars.js";
import { drawClouds, cloudBandBottomY } from "./sky/clouds.js";
import { drawSun, drawMoon, celestialPose } from "./sky/celestial.js";
import { CLOUD_TONES } from "./toon/sky-art.js";
import {
  layoutSeaSparkles,
  layoutShootingStar,
  seaSparkleRgb,
  drawSeaSparkles,
  drawShootingStar,
} from "./sky/sky-fx.js";
import {
  computeMockupUiScale,
  mockupSunRadiusPx,
  mockupMoonRadiusPx,
} from "../layout/mockup-ui-scale.js";
import { resolveGridWidthPx } from "../layout/responsive-board.js";
import { combineSkyDecorSeed, isCelestialDiscVisible } from "../core/day-night.js";
import { computeCelestialState, decomposeHour, hourToColorPhase } from "../core/sky-clock.js";

function scaleUiFromLayout(layout, bounds) {
  var uiW = layout ? resolveGridWidthPx(layout) : bounds.width;
  if (!(uiW > 0)) uiW = bounds.width;
  return computeMockupUiScale(uiW).scaleUi;
}

/**
 * Sun and moon radii (CSS px) the sky draws for a layout and bounds, exactly as
 * buildSkySceneContext sizes them (the loader warm-up bakes the bodies at these radii).
 * @param {object|null} layout
 * @param {{ width: number }} bounds
 * @returns {{ sun: number, moon: number }}
 */
export function skyBodyRadii(layout, bounds) {
  var scaleUi = scaleUiFromLayout(layout || {}, bounds);
  return { sun: mockupSunRadiusPx(scaleUi), moon: mockupMoonRadiusPx(scaleUi) };
}

/**
 * Animation "seconds" that elapse per in-game hour. Hour-driven decor (cloud drift, star
 * drift) runs on this clock, so it is frozen whenever the hour is held still and speeds up
 * exactly when the hour is advanced quickly (the day/night time-lapse transitions).
 */
var ANIM_SECONDS_PER_HOUR = 6;

/** True when the star field should be visible for this hour and layout. */
export function shouldDrawSkyStarsForHour(hour, layout, bounds) {
  if (!bounds) return false;
  layout = layout || {};
  var scaleUi = scaleUiFromLayout(layout, bounds);
  var hourPhase = decomposeHour(hour);
  var bodyRadius = hourPhase.isDay
    ? mockupSunRadiusPx(scaleUi)
    : mockupMoonRadiusPx(scaleUi);
  var state = computeCelestialState(hour, layout, { bodyRadius: bodyRadius });
  if (starFieldAlpha(state.colorPhase) <= 0.01) return false;
  var seaLineY = state.anchors.seaLineY;
  return Math.max(0, seaLineY - bounds.y) > 0;
}

/**
 * Stars over the sky bands. Drift uses the hour-based decor clock (same as drawSkyScene),
 * twinkle the wobble clock.
 */
export function drawSkyStarsForScene(ctx, bounds, hour, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var layout = opts.layout || {};
  var runSeed = opts.runSeed || 0;
  var scaleUi = scaleUiFromLayout(layout, bounds);
  var hourPhase = decomposeHour(hour);
  var bodyRadius = hourPhase.isDay
    ? mockupSunRadiusPx(scaleUi)
    : mockupMoonRadiusPx(scaleUi);
  var state = computeCelestialState(hour, layout, { bodyRadius: bodyRadius });
  var seaLineY = state.anchors.seaLineY;
  var skyBand = {
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: Math.max(0, seaLineY - bounds.y),
  };
  if (skyBand.height <= 0) return;
  var animMs = state.hour * ANIM_SECONDS_PER_HOUR * 1000;
  drawStars(ctx, skyBand, state.colorPhase, animMs, runSeed, {
    dpr: opts.dpr,
    legacy: opts.starLegacy === true,
    wobbleSec: Number(opts.wobbleSec) || 0,
  });
}

/**
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} hour
 * @param {object} [opts]
 */
export function buildSkySceneContext(bounds, hour, opts) {
  opts = opts || {};
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return null;

  var layout = opts.layout || {};
  var runSeed = opts.runSeed || 0;
  var scaleUi = scaleUiFromLayout(layout, bounds);
  var hourPhase = decomposeHour(hour);
  var bodyRadius = hourPhase.isDay
    ? mockupSunRadiusPx(scaleUi)
    : mockupMoonRadiusPx(scaleUi);
  var state = computeCelestialState(hour, layout, { bodyRadius: bodyRadius });
  var seaLineY = state.anchors.seaLineY;
  var animSec = state.hour * ANIM_SECONDS_PER_HOUR;
  var animMs = animSec * 1000;
  var cloudSeed = combineSkyDecorSeed(runSeed, 0);
  var skyBand = {
    x: bounds.x,
    y: bounds.y,
    width: w,
    height: Math.max(0, seaLineY - bounds.y),
  };
  var pos = state.position;
  var discVisible = isCelestialDiscVisible(
    pos.y,
    seaLineY,
    bodyRadius,
    state.bodyProgress
  );
  var emergence = bodyRadius > 0
    ? clamp01((seaLineY - (pos.y - bodyRadius)) / (2 * bodyRadius))
    : (discVisible ? 1 : 0);
  var wobbleSec = Number(opts.wobbleSec) || 0;
  var pose = celestialPose({
    isDay: state.isDay,
    x: pos.x,
    y: pos.y,
    r: bodyRadius,
    seaLineY: seaLineY,
    colorPhase: state.colorPhase,
    wobbleSec: wobbleSec,
    skyRgb: skyBand.height > 0
      ? skyColorAt(state.colorPhase, (pos.y - bounds.y) / skyBand.height)
      : null,
  });

  return {
    bounds: bounds,
    layout: layout,
    runSeed: runSeed,
    scaleUi: scaleUi,
    state: state,
    t: state.colorPhase,
    seaLineY: seaLineY,
    animSec: animSec,
    animMs: animMs,
    cloudSeed: cloudSeed,
    skyBand: skyBand,
    pos: pos,
    bodyRadius: bodyRadius,
    discVisible: discVisible,
    emergence: emergence,
    wobbleSec: wobbleSec,
    pose: pose,
  };
}

/**
 * Sky bands + stars (behind moon/clouds). Cached separately in the live game.
 */
export function drawSkyBackdropScene(ctx, bounds, hour, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var scene = buildSkySceneContext(bounds, hour, opts);
  if (!scene) return;
  drawSky(ctx, bounds, scene.t, { seaLineY: scene.seaLineY, scaleUi: scene.scaleUi });
  if (opts.drawStars !== false) {
    drawSkyStarsForScene(ctx, bounds, hour, {
      layout: scene.layout,
      runSeed: scene.runSeed,
      dpr: opts.dpr,
      starLegacy: opts.starLegacy === true,
      wobbleSec: scene.wobbleSec,
    });
    drawShootingStar(ctx, skySceneFx(scene, opts).shootingStar);
  }
}

/**
 * Ambient effects of one frame (sea sparkles, the night's shooting star), shared by the
 * Canvas 2D scene and the GPU sky layer.
 * @param {object} scene from buildSkySceneContext
 * @param {{ drawCelestial?: boolean, drawStars?: boolean }} [opts]
 */
export function skySceneFx(scene, opts) {
  opts = opts || {};
  var bounds = scene.bounds;
  var showCelestial = opts.drawCelestial !== false;
  var seaH = Math.max(0, bounds.y + bounds.height - scene.seaLineY);
  var e = scene.emergence;
  var eased = e * e * (3 - 2 * e);
  var glareOn = showCelestial && scene.discVisible && e > 0.001;
  var isMoon = !scene.state.isDay;
  return {
    sparkles: layoutSeaSparkles({
      width: bounds.width,
      seaLineY: scene.seaLineY,
      seaH: seaH,
      scaleUi: scene.scaleUi,
      wobbleSec: scene.wobbleSec,
      glareX: scene.pos.x,
      glareStrength: glareOn ? (isMoon ? 0.85 : 1) * eased : 0,
      glareHalf: (isMoon ? 58 : 76) * scene.scaleUi * (0.22 + 0.78 * eased),
    }),
    sparkleRgb: seaSparkleRgb(isMoon, scene.pose.sunset),
    shootingStar: opts.drawStars === false ? null : layoutShootingStar({
      width: bounds.width,
      skyH: scene.skyBand.height,
      scaleUi: scene.scaleUi,
      wobbleSec: scene.wobbleSec,
      starAlpha: starFieldAlpha(scene.t),
    }),
  };
}

/**
 * Moon/sun, clouds, sea, and glare — composited over the backdrop.
 */
export function drawSkyForegroundScene(ctx, bounds, hour, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var scene = buildSkySceneContext(bounds, hour, opts);
  if (!scene) return;

  var state = scene.state;
  var t = scene.t;
  var seaLineY = scene.seaLineY;
  var animMs = scene.animMs;
  var cloudSeed = scene.cloudSeed;
  var skyBand = scene.skyBand;
  var pos = scene.pos;
  var bodyRadius = scene.bodyRadius;
  var discVisible = scene.discVisible;
  var emergence = scene.emergence;
  var layout = scene.layout;
  var scaleUi = scene.scaleUi;
  var wobbleSec = scene.wobbleSec;
  var pose = scene.pose;

  var showCelestial = opts.drawCelestial !== false;
  var showClouds = opts.drawClouds !== false;

  // 1. Active body — drawn BEFORE the clouds so they pass in front of it, and clipped to the
  //    sky band so the inked horizon cleanly cuts the disc as it sets. The gate allows for
  //    the tallest wobble stretch, so the body never appears out of nowhere.
  if (showCelestial && skyBand.height > 0 && pose.visible) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(skyBand.x, skyBand.y, skyBand.width, skyBand.height);
    ctx.clip();
    var bodyOpts = {
      wobbleSec: wobbleSec,
      seaLineY: seaLineY,
      dpr: opts.dpr || 1,
    };
    if (state.isDay) {
      drawSun(ctx, pos.x, pos.y, bodyRadius, t, bodyOpts);
    } else {
      // The moon's dark side takes the colour of the sky band behind it.
      bodyOpts.skyColor = skyColorAt(t, (pos.y - bounds.y) / skyBand.height);
      drawMoon(ctx, pos.x, pos.y, bodyRadius, bodyOpts);
    }
    ctx.restore();
  }

  // 2. Clouds drift in front of the sun/moon. Day white turns to night slate with the hour,
  //    with a golden-hour wash around sunrise and sunset.
  if (showClouds) {
    var cloudBottomY = cloudBandBottomY(layout, bounds);
    // Light the clouds from the visible body: warm from the sun, cool from the
    // moon, brighter the closer/higher it is and fading out as it sets.
    var cloudLight = showCelestial && discVisible
      ? {
          x: pos.x,
          y: pos.y,
          glowRadius: bodyRadius * 8,
          color: state.isDay ? [255, 232, 158] : [200, 220, 255],
          strength: (state.isDay ? 1.05 : 0.5) * (0.3 + 0.7 * emergence),
        }
      : null;
    var warm = cloudWarm(state.phase);
    drawClouds(ctx, bounds, animMs, cloudSeed, {
      layout: layout,
      timeOfDay: t,
      cloudDayBoundary: cloudDayBoundary(state.phase),
      cloudBandBottomY: cloudBottomY,
      cloudLight: cloudLight,
      cloudWarmth: warm.amount,
      cloudWarmColors: warm.colors,
      wobbleSec: wobbleSec,
    });
  }

  // 3. Sea on top of the band edge — the swell laps on the wobble clock. Painting it after
  //    the body is what hides the submerged part of the disc.
  drawSea(ctx, bounds, t, { seaLineY: seaLineY, waveTime: wobbleSec, scaleUi: scaleUi });

  // 4. Glare column under whichever body is up — grows wider and fades in as the
  //    body clears the horizon (thin at the first sliver, full once risen).
  if (showCelestial && discVisible && emergence > 0.001) {
    var eased = emergence * emergence * (3 - 2 * emergence); // smoothstep
    drawSeaGlare(ctx, bounds, {
      celestialX: pos.x,
      seaLineY: seaLineY,
      isMoon: !state.isDay,
      waveTime: wobbleSec,
      strength: (state.isDay ? 1 : 0.85) * eased,
      widthScale: 0.22 + 0.78 * eased,
      scaleUi: scaleUi,
      sunset: pose.sunset,
    });
  }

  // 5. Sparkles popping on the water, thickest in the glare.
  var fx = skySceneFx(scene, opts);
  drawSeaSparkles(ctx, fx.sparkles, fx.sparkleRgb);
}

/**
 * Draw the full sky/sea scene for a continuous hour.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds full canvas
 * @param {number} hour continuous day/night hour (0..72+)
 * @param {{
 *   layout: object,        // { coreRect, playArea, leftStrip, rightStrip }
 *   runSeed?: number,      // per-run seed; varies decor per run
 *   drawStars?: boolean,   // default true
 *   drawClouds?: boolean,  // default true
 *   drawCelestial?: boolean, // sun/moon + sea glare; default true
 *   wobbleSec?: number,    // pause-aware wobble clock (s): squash, morph, twinkle, swell
 *   dpr?: number,          // bake density for the sun/moon pictures
 * }} opts
 */
export function drawSkyScene(ctx, bounds, hour, opts) {
  drawSkyBackdropScene(ctx, bounds, hour, opts);
  drawSkyForegroundScene(ctx, bounds, hour, opts);
}

function clamp01(v) {
  if (v <= 0) return 0;
  if (v >= 1) return 1;
  return v;
}

/**
 * Day/night boundary for the clouds (0 = all day/white, 1 = all night-coloured).
 *  - phase 0..2:  sunrise — clouds whiten, fully white by hour 2.
 *  - phase 2..10: full day (white).
 *  - phase 10..12: sunset — clouds darken toward the night slate.
 *  - phase 12..24: full night colour.
 * @param {number} phase in-cycle hour 0..24
 */
export function cloudDayBoundary(phase) {
  if (phase < 2) return 1 - phase / 2;
  if (phase < 10) return 0;
  if (phase < 12) return (phase - 10) / 2;
  return 1;
}

// Golden-hour cloud tone sets: sunrise leans peach-gold, sunset rose-orange.
var SUNRISE_WARM = CLOUD_TONES.sunrise;
var SUNSET_WARM = CLOUD_TONES.sunset;

/**
 * Golden-hour warmth for the clouds: intensity (0 = none, 1 = peak) plus the
 * tone set to tint with. Intensity is a smooth bump around sunrise (phase ~1) and
 * sunset (phase ~11.5), zero through midday and the deep night; a third bump near
 * phase 24 covers the pre-dawn glow that wraps to the next cycle's sunrise. The
 * sunrise/pre-dawn bumps use the gold set, sunset the rose one.
 * @param {number} phase in-cycle hour 0..24
 * @returns {{ amount: number, colors: { ink: number[], shade: number[], base: number[], light: number[] } }}
 */
export function cloudWarm(phase) {
  var rise = Math.max(warmBump(phase - 1), warmBump(phase - 24));
  var set = warmBump(phase - 11.5);
  return {
    amount: clamp01(Math.max(rise, set)),
    colors: rise >= set ? SUNRISE_WARM : SUNSET_WARM,
  };
}

/**
 * Hour → draw opts for a single cloud (day/night sweep, golden wash).
 * Does not include `cloudLight` — callers place a synthetic or scene light.
 * @param {number} hour continuous day/night hour
 * @returns {{
 *   timeOfDay: number,
 *   cloudDayBoundary: number,
 *   cloudWarmth: number,
 *   cloudWarmColors: { ink: number[], shade: number[], base: number[], light: number[] },
 *   isDay: boolean,
 *   phase: number,
 *   bodyProgress: number,
 * }}
 */
export function cloudAppearanceForHour(hour) {
  var d = decomposeHour(hour);
  var warm = cloudWarm(d.phase);
  return {
    timeOfDay: hourToColorPhase(hour),
    cloudDayBoundary: cloudDayBoundary(d.phase),
    cloudWarmth: warm.amount,
    cloudWarmColors: warm.colors,
    isDay: d.isDay,
    phase: d.phase,
    bodyProgress: d.bodyProgress,
  };
}

/** Smooth 0..1 bump, 1 at delta=0, fading to 0 over a ±3h half-width. */
function warmBump(delta) {
  var d = Math.abs(delta) / 3;
  if (d >= 1) return 0;
  var u = 1 - d;
  return u * u * (3 - 2 * u);
}
